import {
    ChangeDetectionStrategy,
    Component,
    OnChanges,
    OnInit,
    SimpleChanges,
    TemplateRef,
    input,
    output,
    viewChild,
    model,
    computed,
} from '@angular/core'
import { TbCheckboxComponent } from '@components-library/tb-checkbox'

import { TbButtonComponent } from '../../tb-button/tb-button.component'
import { TbMoreMenuComponent } from '@components-library/tb-more-menu/tb-more-menu.component'

export interface CollapseCallType {
    click?: boolean
    check: boolean
}

@Component({
    selector: 'app-tb-board-column-header',
    templateUrl: './tb-board-column-header.component.html',
    styleUrls: ['./tb-board-column-header.component.sass'],
    imports: [TbButtonComponent, TbCheckboxComponent, TbMoreMenuComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TbBoardColumnHeaderComponent<Item, Column> implements OnInit, OnChanges {
    readonly items = input.required<Item[]>()
    readonly columnItem = input.required<Column>()

    readonly selectedItems = input<Item[]>([])
    readonly controlCollapse = input(false)

    readonly moreMenu = input<TemplateRef<any>>()

    readonly collapsed = model(false)

    readonly collapseChanges = output<CollapseCallType>()
    readonly selectColumn = output<boolean>()

    readonly menu = viewChild.required(TbMoreMenuComponent)

    toggleCollapseBound = this.setCollapse.bind(this)

    readonly headerMenuClass = computed(() => ({
        'visible': true,
        'group-hover/board-column-header:visible': !this.menu()?.menuOpen,
        'md:invisible': !this.menu()?.menuOpen,
        'mr-2': !this.collapsed(),
        'mb-2': this.collapsed(),
    }))

    readonly selectCheckboxClass = computed(() => [
        this.selectedItems().length > 0 ? 'visible' : 'md:invisible',
        this.collapsed() ? 'mb-2' : 'mr-2',
    ])

    readonly stateClass = computed(() => {
        const baseClass = this.collapsed() ? 'collapsed w-[2.5rem]' : 'h-[2.5rem] mb-2'
        return `${baseClass} bg-neutral1`
    })

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.items) {
            this.checkCollapsedState()
        }
    }

    ngOnInit(): void {
        this.checkCollapsedState()
    }

    setCollapse() {
        this.collapsed.set(!this.collapsed())
        this.collapseChanges.emit({ click: true, check: this.collapsed() })
    }

    checkCollapsedState() {
        if (this.controlCollapse()) return

        const collapsed = this.items().length === 0
        if (collapsed === this.collapsed()) return

        this.collapsed.set(collapsed)
        this.collapseChanges.emit({ check: this.collapsed() })
    }

    get isAllItemsSelected() {
        return this.items().every((item) =>
            this.selectedItems().find((selectedItem) => selectedItem === item),
        )
    }

    get isSomeItemsSelected() {
        return this.items().some((item) =>
            this.selectedItems().find((selectedItem) => selectedItem === item),
        )
    }

    get isIndeterminate() {
        return !this.isAllItemsSelected && this.isSomeItemsSelected
    }
}
